import * as THREE from "three";
import {
  createChaseCameraResetLifecycle,
  createSuntraceChaseCamera,
  type ChaseCameraResetLifecycle,
} from "./camera";
import {
  createSuntraceColorGrade,
  sampleSuntraceColorGrade,
  type SuntraceColorGrade,
} from "./colorScript";
import {
  loadSuntraceEnvironmentAssets,
  type SuntraceEnvironmentAssets,
} from "./environmentAssets";
import { createSuntraceEffects } from "./effects";
import { createSuntraceMaterialLibrary } from "./materials";
import {
  createSuntracePostProcessor,
  type SuntracePostProcessor,
} from "./postprocessing";
import { createSuntraceQualityManager } from "./quality";
import { createSuntraceTrack } from "./track";
import type { TrackFrame } from "./track";
import type {
  RacePresentationSnapshot,
  SuntraceCameraMode,
  SuntraceQuality,
  SuntraceQualityPreference,
  SuntracePresentationMotionMetrics,
  SuntraceRenderMetrics,
} from "./types";
import { loadSuntraceHeroVehicleAssetOrFallback } from "./vehicleAssets";
import type { SuntraceAuthoredVehicleAsset } from "./vehicleAssets";
import {
  createSuntraceVehicleFleet,
  type SuntraceVehicleFleet,
} from "./vehicles";
import { createSuntraceWorld, type SuntraceWorld } from "./world";

export type SuntraceTrackInfo = {
  length: number;
  width: number;
  roadHalfWidth: number;
};

export type SuntraceSceneControllerOptions = {
  canvas: HTMLCanvasElement;
  quality?: SuntraceQualityPreference;
  adaptiveQuality?: boolean;
  autoResize?: boolean;
  onQualityChange?: (quality: SuntraceQuality, metrics: SuntraceRenderMetrics) => void;
  onContextLost?: () => void;
  onContextRestored?: () => void;
  signal?: AbortSignal;
  onAssetProgress?: (progress: number) => void;
};

export type SuntraceSceneController = {
  render: (snapshot: RacePresentationSnapshot, deltaSeconds: number) => void;
  resize: (width?: number, height?: number, pixelRatio?: number) => void;
  setQuality: (preference: SuntraceQualityPreference) => void;
  resetPresentation: () => void;
  cycleCamera: () => SuntraceCameraMode;
  getCameraMode: () => SuntraceCameraMode;
  getTrackInfo: () => SuntraceTrackInfo;
  getMetrics: () => SuntraceRenderMetrics;
  dispose: () => void;
};

export const reapplySuntraceShadowContext = (
  shadowMap: Pick<THREE.WebGLRenderer["shadowMap"], "enabled" | "needsUpdate">,
  quality: SuntraceQuality,
  world: Pick<SuntraceWorld, "setQuality">,
): void => {
  shadowMap.enabled = quality !== "low";
  shadowMap.needsUpdate = true;
  world.setQuality(quality);
};

export const routeSuntraceImageDepthAtmosphere = (
  fog: THREE.Fog | THREE.FogExp2 | null,
  colorGrade: Pick<SuntraceColorGrade, "hemisphereSkyColor">,
  sun: Pick<THREE.DirectionalLight, "position" | "target">,
  postProcessor: Pick<SuntracePostProcessor, "setAtmosphere">,
  sunDirection: THREE.Vector3,
): boolean => {
  if (!(fog instanceof THREE.Fog)) {
    return false;
  }
  sunDirection.subVectors(sun.position, sun.target.position);
  const sunLengthSquared = sunDirection.lengthSq();
  if (Number.isFinite(sunLengthSquared) && sunLengthSquared > 0) {
    sunDirection.normalize();
  }
  postProcessor.setAtmosphere({
    fogColor: fog.color,
    environmentColor: colorGrade.hemisphereSkyColor,
    fogNear: fog.near,
    fogFar: fog.far,
    sunDirection,
  });
  return true;
};

const getCanvasSize = (
  canvas: HTMLCanvasElement,
): { width: number; height: number } => ({
  width: Math.max(1, Math.round(canvas.clientWidth || canvas.width || 1)),
  height: Math.max(1, Math.round(canvas.clientHeight || canvas.height || 1)),
});

const getPixelRatioLimit = (quality: SuntraceQuality): number =>
  quality === "high" ? 2 : quality === "medium" ? 1.45 : 1;

const getFinite = (value: number, fallback = 0): number =>
  Number.isFinite(value) ? value : fallback;

const getNormalized = (value: number): number =>
  THREE.MathUtils.clamp(getFinite(value), 0, 1);

const getSignedNormalized = (value: number): number =>
  THREE.MathUtils.clamp(getFinite(value), -1, 1);

export const resetSuntraceSceneMotion = (
  cameraResetLifecycle: Pick<ChaseCameraResetLifecycle, "requestReset">,
  fleet: Pick<SuntraceVehicleFleet, "resetPresentation">,
): void => {
  cameraResetLifecycle.requestReset();
  fleet.resetPresentation();
};

export const createSuntraceSceneController = (
  options: SuntraceSceneControllerOptions,
): Promise<SuntraceSceneController> => {
  return createSuntraceSceneControllerAsync(options);
};

const createSuntraceSceneControllerAsync = async (
  options: SuntraceSceneControllerOptions,
): Promise<SuntraceSceneController> => {
  const canvas = options.canvas;
  const size = getCanvasSize(canvas);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    depth: true,
    stencil: false,
    powerPreference: "high-performance",
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.86;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.info.autoReset = false;
  renderer.setClearColor(0x8ea1a7, 1);

  const scene = new THREE.Scene();
  scene.name = "SUNTRACE presentation";
  scene.fog = new THREE.Fog(0x8ea1a7, 310, 1_050);
  const materialLibrary = createSuntraceMaterialLibrary(renderer);
  scene.background = materialLibrary.environmentTexture;
  scene.backgroundBlurriness = 0.015;
  scene.backgroundIntensity = 0.5;
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader();
  const environmentTarget = pmremGenerator.fromEquirectangular(
    materialLibrary.environmentTexture,
  );
  pmremGenerator.dispose();
  scene.environment = environmentTarget.texture;
  scene.environmentIntensity = 0.68;

  let environmentAssets: SuntraceEnvironmentAssets | null = null;
  let heroVehicleAsset: SuntraceAuthoredVehicleAsset | null = null;
  let assetMode: SuntraceRenderMetrics["assetMode"] = "procedural-fallback";
  try {
    environmentAssets = await loadSuntraceEnvironmentAssets(
      renderer,
      options.signal,
      options.onAssetProgress,
    );
    assetMode = "authored";
  } catch (error) {
    if (
      options.signal?.aborted ||
      (error instanceof DOMException && error.name === "AbortError")
    ) {
      materialLibrary.dispose();
      environmentTarget.dispose();
      scene.environment = null;
      scene.background = null;
      scene.clear();
      renderer.dispose();
      throw error;
    }
  }

  try {
    heroVehicleAsset = await loadSuntraceHeroVehicleAssetOrFallback(
      renderer,
      options.signal,
    );
    if (heroVehicleAsset === null) {
      assetMode = "procedural-fallback";
    }
  } catch (error) {
    assetMode = "procedural-fallback";
    if (
      options.signal?.aborted ||
      (error instanceof DOMException && error.name === "AbortError")
    ) {
      environmentAssets?.dispose();
      environmentAssets = null;
      materialLibrary.dispose();
      environmentTarget.dispose();
      scene.environment = null;
      scene.background = null;
      scene.clear();
      renderer.dispose();
      throw error;
    }
  }

  const track = createSuntraceTrack();
  const colorGrade = createSuntraceColorGrade();
  const world = createSuntraceWorld(track, materialLibrary, environmentAssets);
  const postSunDirection = new THREE.Vector3();
  const fleet = createSuntraceVehicleFleet(heroVehicleAsset);
  const effects = createSuntraceEffects();
  scene.add(world.group, fleet.group, effects.group);
  const chaseCamera = createSuntraceChaseCamera(size.width, size.height);
  const cameraResetLifecycle = createChaseCameraResetLifecycle(chaseCamera);
  const postProcessor = createSuntracePostProcessor(
    renderer,
    scene,
    chaseCamera.camera,
    size.width,
    size.height,
  );
  const qualityManager = createSuntraceQualityManager(
    options.quality ?? "auto",
    options.adaptiveQuality ?? true,
  );
  const heroFrame = track.sample(0);
  const rivalFrames: TrackFrame[] = Array.from({ length: 5 }, () => track.sample(0));
  const trackInfo: SuntraceTrackInfo = {
    length: track.length,
    width: track.width,
    roadHalfWidth: track.width * 0.5 - 0.25,
  };
  let currentWidth = size.width;
  let currentHeight = size.height;
  let requestedPixelRatio = window.devicePixelRatio;
  let currentPixelRatio = 1;
  let contextLost = false;
  let disposed = false;
  let presentationTime = 0;
  let hasPresentationTime = false;
  let resizeObserver: ResizeObserver | null = null;
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reducedMotion = reducedMotionQuery.matches;

  const getMetrics = (): SuntraceRenderMetrics => {
    const environmentState = world.getEnvironmentState();
    const effectsDiagnostics = effects.getDiagnostics();
    const postMetrics = postProcessor.getMetrics();
    const motion: SuntracePresentationMotionMetrics = {
      cameraRoll: chaseCamera.getCameraRoll(),
      sprayActive: effectsDiagnostics.sprayActive,
      roadFlowActive:
        effectsDiagnostics.roadFlowVisible && effectsDiagnostics.roadFlowOpacity > 0,
      speedStreaksActive: effectsDiagnostics.speedStreaksActive,
    };
    return {
      quality: qualityManager.quality,
      averageFrameTime: qualityManager.averageFrameTime,
      pixelRatio: currentPixelRatio,
      drawCalls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      contextLost,
      assetMode,
      environmentWeathering: environmentState.weatheringEnabled,
      environmentDecals: environmentState.decalsVisible,
      shadowMapSize: world.sun.shadow.mapSize.width,
      authoredDensity: environmentState.authoredDensity,
      ...postMetrics,
      motion,
      reducedMotion: {
        ...motion,
        enabled: reducedMotion,
      },
    };
  };

  const resize = (
    width = getCanvasSize(canvas).width,
    height = getCanvasSize(canvas).height,
    pixelRatio = window.devicePixelRatio,
  ): void => {
    if (disposed) {
      return;
    }
    currentWidth = Math.max(1, Math.round(width));
    currentHeight = Math.max(1, Math.round(height));
    requestedPixelRatio = Math.max(0.5, getFinite(pixelRatio, 1));
    currentPixelRatio = Math.min(
      requestedPixelRatio,
      getPixelRatioLimit(qualityManager.quality),
    );
    renderer.setPixelRatio(currentPixelRatio);
    renderer.setSize(currentWidth, currentHeight, false);
    chaseCamera.resize(currentWidth, currentHeight);
    postProcessor.resize(currentWidth, currentHeight, currentPixelRatio);
  };

  const applyQuality = (
    quality: SuntraceQuality,
    notify: boolean,
    restoringContext = false,
  ): void => {
    if (restoringContext) {
      reapplySuntraceShadowContext(renderer.shadowMap, quality, world);
      postProcessor.restoreContext();
    } else {
      renderer.shadowMap.enabled = quality !== "low";
      world.setQuality(quality);
    }
    fleet.setQuality(quality);
    effects.setQuality(quality);
    postProcessor.setQuality(quality);
    resize(currentWidth, currentHeight, requestedPixelRatio);
    if (notify) {
      options.onQualityChange?.(quality, getMetrics());
    }
  };

  const onContextLost = (event: Event): void => {
    event.preventDefault();
    contextLost = true;
    options.onContextLost?.();
  };

  const onContextRestored = (): void => {
    contextLost = false;
    applyQuality(qualityManager.quality, false, true);
    options.onContextRestored?.();
  };

  const onReducedMotionChange = (event: MediaQueryListEvent): void => {
    reducedMotion = event.matches;
  };

  const onWindowResize = (): void => {
    resize();
  };

  canvas.addEventListener("webglcontextlost", onContextLost, false);
  canvas.addEventListener("webglcontextrestored", onContextRestored, false);
  reducedMotionQuery.addEventListener("change", onReducedMotionChange);
  if (options.autoResize ?? true) {
    window.addEventListener("resize", onWindowResize, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry !== undefined && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          resize(entry.contentRect.width, entry.contentRect.height);
        }
      });
      resizeObserver.observe(canvas);
    }
  }

  applyQuality(qualityManager.quality, false);

  return {
    render(snapshot, deltaSeconds) {
      if (disposed || contextLost) {
        return;
      }
      const delta = THREE.MathUtils.clamp(getFinite(deltaSeconds, 1 / 60), 0, 0.05);
      const presentationFrozen = snapshot.phase === "paused" || snapshot.phase === "finished";
      const motionDelta = presentationFrozen ? 0 : delta;
      const trackProgress = getFinite(snapshot.trackProgress);
      const lateralOffset = THREE.MathUtils.clamp(
        getFinite(snapshot.lateralOffset),
        -trackInfo.roadHalfWidth,
        trackInfo.roadHalfWidth,
      );
      const speed = Math.max(0, getFinite(snapshot.speed));
      const heading = THREE.MathUtils.clamp(getFinite(snapshot.heading), -0.5, 0.5);
      const steering = getSignedNormalized(snapshot.steering);
      const slip = getSignedNormalized(snapshot.slip);
      const drift = getNormalized(snapshot.drift);
      const charge = getNormalized(snapshot.charge);
      const boost = getNormalized(snapshot.boost);
      const impact = getNormalized(snapshot.impact);
      const impactSide = snapshot.impactSide === -1 || snapshot.impactSide === 1
        ? snapshot.impactSide
        : 0;
      const time = Math.max(0, getFinite(snapshot.time));
      if (!hasPresentationTime || !presentationFrozen) {
        presentationTime = time;
        hasPresentationTime = true;
      }
      track.sample(trackProgress, lateralOffset, heroFrame);
      cameraResetLifecycle.applyReset(heroFrame);
      fleet.hero.setPresentation({
        frame: heroFrame,
        speed,
        heading,
        steering,
        slip,
        drift,
        boost,
        impact,
        impactSide,
        time: presentationTime,
        deltaSeconds: motionDelta,
        reducedMotion,
      });

      for (let index = 0; index < fleet.rivals.length; index += 1) {
        const rivalVisual = fleet.rivals[index];
        const rivalFrame = rivalFrames[index];
        if (rivalVisual === undefined || rivalFrame === undefined) {
          continue;
        }
        const rivalSnapshot = snapshot.rivals[index];
        if (rivalSnapshot !== undefined) {
          const attractGrid = snapshot.phase === "attract";
          const gridRow = Math.floor(index / 2) + 1;
          const gridSide = index === 4 ? 0 : index % 2 === 0 ? -1 : 1;
          const rivalProgress = attractGrid
            ? trackProgress + gridRow * 0.0052
            : getFinite(rivalSnapshot.trackProgress);
          const rivalLateralOffset = attractGrid
            ? gridSide * 2.45
            : getFinite(rivalSnapshot.lateralOffset);
          track.sample(
            rivalProgress,
            THREE.MathUtils.clamp(
              rivalLateralOffset,
              -trackInfo.roadHalfWidth,
              trackInfo.roadHalfWidth,
            ),
            rivalFrame,
          );
          const progressDistance =
            Math.abs(((rivalProgress - trackProgress + 1.5) % 1) - 0.5) *
            track.length;
          const portraitEmphasis =
            currentWidth / currentHeight < 0.72
              ? 1 + THREE.MathUtils.smoothstep(progressDistance, 12, 72) * 0.55
              : 1;
          rivalVisual.setDistanceEmphasis(portraitEmphasis);
          rivalVisual.setVisible(rivalSnapshot.visible ?? true);
          rivalVisual.setPresentation({
            frame: rivalFrame,
            speed: Math.max(0, getFinite(rivalSnapshot.speed)),
            heading: THREE.MathUtils.clamp(getFinite(rivalSnapshot.heading), -0.5, 0.5),
            steering: getSignedNormalized(rivalSnapshot.steering),
            slip: getSignedNormalized(rivalSnapshot.slip),
            drift: Math.abs(getSignedNormalized(rivalSnapshot.slip)),
            boost: getNormalized(rivalSnapshot.boost),
            impact: 0,
            impactSide: 0,
            time: presentationTime,
            deltaSeconds: motionDelta,
            reducedMotion,
          });
          continue;
        }
        const showGrid = snapshot.phase === "attract" || snapshot.phase === "countdown";
        rivalVisual.setVisible(showGrid);
        rivalVisual.setDistanceEmphasis(currentWidth / currentHeight < 0.72 ? 1.32 : 1);
        if (showGrid) {
          const gridRow = Math.floor(index / 2) + 1;
          const gridSide = index % 2 === 0 ? -1 : 1;
          track.sample(
            trackProgress + gridRow * 0.0054,
            gridSide * (2.15 + (gridRow % 2) * 0.28),
            rivalFrame,
          );
          rivalVisual.setPresentation({
            frame: rivalFrame,
            speed: speed * 0.85,
            heading: 0,
            steering: 0,
            slip: 0,
            drift: 0,
            boost: 0,
            impact: 0,
            impactSide: 0,
            time: presentationTime,
            deltaSeconds: motionDelta,
            reducedMotion,
          });
        }
      }

      const safeSnapshot: RacePresentationSnapshot = {
        trackProgress,
        lateralOffset,
        speed,
        heading,
        steering,
        slip,
        drift,
        charge,
        boost,
        impact,
        impactSide,
        rivals: snapshot.rivals,
        phase: snapshot.phase,
        time: presentationTime,
      };
      sampleSuntraceColorGrade(trackProgress, colorGrade);
      scene.fog?.color.copy(colorGrade.fogColor);
      if (scene.fog instanceof THREE.Fog) {
        scene.fog.near = colorGrade.fogNear;
        scene.fog.far = colorGrade.fogFar;
      }
      scene.backgroundIntensity = colorGrade.backgroundIntensity;
      scene.environmentIntensity = colorGrade.environmentIntensity;
      world.update(
        presentationTime,
        Math.max(charge, boost),
        heroFrame.position,
        colorGrade,
        trackProgress,
      );
      routeSuntraceImageDepthAtmosphere(
        scene.fog,
        colorGrade,
        world.sun,
        postProcessor,
        postSunDirection,
      );
      effects.update(safeSnapshot, heroFrame, motionDelta, reducedMotion);
      chaseCamera.update(safeSnapshot, heroFrame, motionDelta, reducedMotion);
      postProcessor.update(safeSnapshot, reducedMotion, colorGrade, motionDelta);
      renderer.info.reset();
      postProcessor.render(motionDelta);
      if (motionDelta > 0) {
        const qualityChange = qualityManager.sample(motionDelta);
        if (qualityChange !== null) {
          applyQuality(qualityChange, true);
        }
      }
    },
    resize,
    setQuality(preference) {
      const quality = qualityManager.setPreference(preference);
      applyQuality(quality, true);
    },
    resetPresentation() {
      hasPresentationTime = false;
      resetSuntraceSceneMotion(cameraResetLifecycle, fleet);
    },
    cycleCamera() {
      return chaseCamera.cycleMode();
    },
    getCameraMode() {
      return chaseCamera.getMode();
    },
    getTrackInfo() {
      return { ...trackInfo };
    },
    getMetrics,
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      resizeObserver?.disconnect();
      resizeObserver = null;
      window.removeEventListener("resize", onWindowResize);
      reducedMotionQuery.removeEventListener("change", onReducedMotionChange);
      canvas.removeEventListener("webglcontextlost", onContextLost, false);
      canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
      postProcessor.dispose();
      effects.dispose();
      fleet.dispose();
      world.dispose();
      environmentAssets?.dispose();
      environmentAssets = null;
      materialLibrary.dispose();
      environmentTarget.dispose();
      scene.environment = null;
      scene.background = null;
      scene.clear();
      renderer.setAnimationLoop(null);
      renderer.dispose();
    },
  };
};
